// Client game loop: fixed-rate physics ticks (125 Hz by default) driven by
// keyboard/mouse input, interpolated rendering at the display refresh rate.

import * as THREE from 'three';
import type { Replay } from '../shared/game/replay';
import type { SessionEvent } from '../shared/game/session';
import { TEAM_NAMES, type Team } from '../shared/game/mapLogic';
import type { RemotePlayer, ServerMessage } from '../shared/net/protocol';
import { ANGLE2SHORT, SHORT2ANGLE, angleVectors, type Vec3 } from '../shared/math';
import { BUTTON_ACTIVATE, BUTTON_SPRINT, ENTITYNUM_NONE, WBUTTON_PRONE, type PlayerState, type UserCmd } from '../shared/physics/pmove';
import { MASK_PLAYERSOLID, MASK_SOLID } from '../shared/surfaceflags';
import { detectOverbounce } from '../shared/etjump/overbounce';
import { JumpSpeeds, MaxSpeed, StrafeQuality, UpmoveMeter } from '../shared/etjump/trainers';
import type { MoverSet } from '../shared/game/movers';
import { KEY_BACK, KEY_CROUCH, KEY_FORWARD, KEY_JUMP, KEY_LEFT, KEY_PRONE, KEY_RIGHT, KEY_SPRINT, helpText, type Hud } from '../ui/hud';
import { stripColors } from '../ui/etColors';
import { captureThumbnail } from '../ui/maps/levelshots';
import { bindLabel, keyLabel, type Action } from './binds';
import { FOLLOW_DELAY, FollowView } from './follow';
import { SessionModel } from './sessionModel';
import { thirdPersonView } from './thirdPerson';
import { TjLinesView, tjlStore } from './tjLines';
import { TjlRecorder } from '../shared/etjump/tjl';
import { Ghost } from './ghost';
import type { Input } from './input';
import type { LoadedMap } from './mapLoader';
import type { NetClient } from './net';
import type { PlayerAssets } from './playerAssets';
import { PlayerRenderKit } from './playerModel';
import { ViewWeapon } from './viewWeapon';
import { Prediction } from './prediction';
import { RemotePlayers } from './remotePlayers';
import type { TextureManager } from '../render/textures';
import type { GameAudio } from '../audio/gameAudio';
import { M_PITCH, M_YAW, type Settings, type SettingsStore } from './settings';

const MAX_PITCH = SHORT2ANGLE(16000);
/** The run result panel goes away when the player moves this long after it appeared. */
const RESULT_MOVE_GRACE_MS = 2000;
/** Frames rendered before a missing map thumbnail is captured (textures are loaded by then). */
const THUMBNAIL_FRAMES = 90;
const HELP_AUTO_HIDE_MS = 8000;

export interface RunSplit {
  index: number;
  time: number | null;
}

/** A finished run, for the end-of-run panel. */
export interface RunResult {
  id: number;
  map: string;
  run: string;
  time: number;
  online: boolean;
  splits: RunSplit[];
  replay: Replay | null;
}

/** What the game tells the menus. */
export interface GameUi {
  runResult(r: RunResult): void;
  /** The player moved on: hide the run result panel. */
  dismissRunResult(): void;
  /** A thumbnail of a map without a levelshot was captured. */
  thumbnail(map: string, image: Blob): void;
  spectating(active: boolean): void;
  /** The scoreboard key is held (true) or released. */
  scoreboard(open: boolean): void;
  /** Other players of the room (online), a few times a second. */
  players(players: RemotePlayer[]): void;
}

export interface GameOptions {
  net?: NetClient | null;
  /** Capture a thumbnail once the map is shown (it has no levelshot). */
  wantThumbnail?: boolean;
  /** The player model (other players and the ghost are boxes without it). */
  players?: { assets: PlayerAssets; textures: TextureManager } | null;
  /** The map's sounds (silent without). */
  audio?: GameAudio | null;
  /** Online: the room's clock is performance.now() + this (commands run on it: shared mover phase). */
  roomClockOffset?: number;
}

let nextResultId = 1;

export class Game {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(75, 1, 4, 65536);
  /** Set when playing on a server. */
  readonly net: NetClient | null;
  private running = false;
  private rafId = 0;
  private lastFrame = 0;
  private accumulator = 0;
  private serverTime = 1000;
  /** Client view angles from the mouse (cl.viewangles); the view adds ps.deltaAngles. */
  private viewAngles: Vec3 = [0, 0, 0];
  private prevOrigin: Vec3 = [0, 0, 0];
  private prevViewHeight = 40;
  private lastMove = { forwardmove: 0, rightmove: 0, upmove: 0 };
  private fpsFrames = 0;
  private fpsTime = 0;
  private fps = 0;
  private readonly remotes: RemotePlayers;
  /** Materials and lighting of the player models on this map. */
  private readonly playerKit: PlayerRenderKit | null;
  private readonly prediction = new Prediction();
  private readonly audio: GameAudio | null;
  /** performance.now() + this = the room's clock (online). */
  private readonly roomClockOffset: number | null;
  /** Watched replay: playback speed, paused, and which keys were down last frame (edges). */
  private replaySpeed = 1;
  private replayPaused = false;
  private replayLabel = '';
  private replayKeys = new Set<Action>();
  private offNet: (() => void) | null = null;
  /** Replay being watched (spectator camera), with its own clock. */
  private spectator: Ghost | null = null;
  private spectatorAccumulator = 0;
  private lastResult: RunResult | null = null;
  private resultShownAt = 0;
  private framesToThumbnail: number;
  private helpTimer = 0;
  private scoreboardShown = false;
  private lastPlayersUpdate = 0;
  /** Spectating another player on the server (set once the server confirmed it). */
  private follow: FollowView | null = null;
  /** A spectate request is on (snapshots without our own state start following). */
  private followWanted = false;
  /** The player's own character, drawn in third person. */
  private selfModel: SessionModel | null = null;
  /** The knife in first person (made when first drawn; null without the model). */
  private viewWeapon: ViewWeapon | null = null;
  private selfMoves = 0;
  // ETJump training HUDs (the player's own moves)
  private readonly jumpSpeeds = new JumpSpeeds();
  private readonly upmove = new UpmoveMeter();
  private readonly strafeQuality = new StrafeQuality();
  private readonly maxSpeed = new MaxSpeed();
  /** The upmove of the last jump is filled in while jump is held after it. */
  private pollUpmove = false;
  /** Trickjump line being recorded, and the map's line in the world. */
  private tjlRecorder: TjlRecorder | null = null;
  private readonly tjl = new TjLinesView();

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    readonly map: LoadedMap,
    private readonly input: Input,
    private readonly store: SettingsStore,
    private readonly hud: Hud,
    private readonly ui: GameUi,
    opts: GameOptions = {},
  ) {
    this.net = opts.net ?? null;
    this.roomClockOffset = opts.roomClockOffset ?? null;
    if (this.roomClockOffset !== null) this.serverTime = Math.floor(performance.now() + this.roomClockOffset);
    this.playerKit = opts.players ? new PlayerRenderKit(opts.players.assets, opts.players.textures, map.scene.shared, map.bsp) : null;
    this.remotes = new RemotePlayers(this.playerKit);
    this.audio = opts.audio ?? null;
    if (this.audio) {
      const audio = this.audio;
      this.remotes.onFrame = (id, origin, moveType, frametime, now) => audio.remoteFrame(id, origin, moveType, frametime, now);
      this.remotes.onEvent = (origin, event) => audio.remoteEvent(origin, event);
    }
    this.framesToThumbnail = opts.wantThumbnail ? THUMBNAIL_FRAMES : -1;
    this.camera.up.set(0, 0, 1);
    // ET draws blended surfaces in shader sort order, not back to front
    renderer.setTransparentSort((a, b) => a.groupOrder - b.groupOrder || a.renderOrder - b.renderOrder || a.id - b.id);
    // the whole map must fit inside the far plane (some maps are ~80k units tall)
    this.camera.far = Math.max(65536, Math.ceil(map.scene.worldSize) + 1024);
    this.camera.updateProjectionMatrix();
    this.scene.add(map.scene.root);
    if (map.scene.sky) this.scene.add(map.scene.sky.object);
    // a global fog colours what is not drawn (ydnar: global fog sets the clear colour)
    const globalFog = map.scene.fogs.global;
    this.scene.background = globalFog ? new THREE.Color(...globalFog.color) : new THREE.Color(0x10131a);
    this.scene.add(this.remotes.object);
    this.scene.add(this.tjl.object);
    this.tjl.show(tjlStore.load(map.name));
    if (this.net) this.offNet = this.net.on(this.onNet);
    hud.setContext({ online: !!this.net, noFallDamage: map.logic.rules.noFallDamage > 0 });
    hud.clearRunInfo();

    const ps = map.session.ps;
    this.viewAngles = [ps.viewangles[0], ps.viewangles[1], 0];
    this.prevOrigin = [...ps.origin];
    map.session.on(this.onSessionEvent);
    this.applySettings();
  }

  private get settings(): Settings {
    return this.store.get();
  }

  /** Last finished run (for the pause menu). */
  get lastRun(): RunResult | null {
    return this.lastResult;
  }

  get spectating(): boolean {
    return this.spectator !== null;
  }

  /** Spectating another player of the room. */
  get following(): boolean {
    return this.follow !== null;
  }

  applySettings(): void {
    const s = this.settings;
    // Hor+ field of view: settings.fov is the horizontal fov on a 4:3 screen
    const vfov = 2 * Math.atan(Math.tan((s.fov * Math.PI) / 360) * 0.75);
    this.camera.fov = (vfov * 180) / Math.PI;
    this.camera.updateProjectionMatrix();
    this.map.scene.shared.uBrightness.value = s.brightness;
    this.hud.setHelpText(helpText(s.binds));
    if (!this.helpTimer) this.hud.showHelp(s.showHelp);
    if (this.audio) this.audio.otherPlayers = s.otherPlayerSounds;
    this.tjl.object.visible = s.showTjl;
  }

  resize(width: number, height: number): void {
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.hud.resize(width, height);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    // after a pause the command clock catches up with the room's (it never goes back)
    if (this.roomClockOffset !== null) this.serverTime = Math.max(this.serverTime, Math.floor(performance.now() + this.roomClockOffset));
    this.lastFrame = performance.now();
    this.accumulator = 0;
    this.input.enabled = true;
    this.rafId = requestAnimationFrame(this.frame);
    clearTimeout(this.helpTimer);
    this.helpTimer = 0;
    if (this.settings.showHelp && this.settings.autoHideHelp) {
      this.hud.showHelp(true);
      this.helpTimer = window.setTimeout(() => {
        this.helpTimer = 0;
        this.hud.showHelp(false);
      }, HELP_AUTO_HIDE_MS);
    }
  }

  stop(): void {
    this.running = false;
    this.hud.closeChat();
    this.input.enabled = false;
    this.input.clear();
    cancelAnimationFrame(this.rafId);
    this.stopSpectating();
    if (this.scoreboardShown) {
      this.scoreboardShown = false;
      this.ui.scoreboard(false);
    }
  }

  dispose(): void {
    this.stop();
    clearTimeout(this.helpTimer);
    this.offNet?.();
    this.remotes.dispose();
    this.spectator?.dispose();
    this.selfModel?.dispose();
    this.viewWeapon?.dispose();
    this.tjl.dispose();
    this.playerKit?.dispose();
    this.audio?.dispose();
    this.map.scene.dispose();
  }

  /** Runs a player action as if its key was pressed (menu buttons: restart, ...). */
  perform(a: Action): void {
    const session = this.map.session;
    // save / load slots 1..3 (ETJump "save 1"); the server applies the same action between the same two commands
    const slot = a === 'save2' || a === 'load2' ? 1 : a === 'save3' || a === 'load3' ? 2 : 0;
    const base = a === 'save2' || a === 'save3' ? 'save' : a === 'load2' || a === 'load3' ? 'load' : a;
    const netAction = base === 'save' || base === 'load' || base === 'respawn' || base === 'noclip' ? base : null;
    if (netAction) this.net?.sendAction(netAction, slot);
    switch (base) {
      case 'save':
        session.save(slot);
        break;
      case 'load':
        session.load(slot);
        this.snapInterpolation();
        break;
      case 'respawn':
        session.respawn();
        this.snapInterpolation();
        this.resetTrainers();
        break;
      case 'noclip':
        session.toggleNoclip();
        break;
      case 'help':
        clearTimeout(this.helpTimer);
        this.helpTimer = 0;
        this.store.update({ showHelp: !this.hud.helpShown });
        break;
      case 'debug':
        this.store.update({ hud: { ...this.settings.hud, debug: !this.settings.hud.debug } });
        break;
      case 'chat':
        this.openChat();
        break;
      case 'voteYes':
      case 'voteNo':
        this.net?.vote(a === 'voteYes');
        break;
      case 'replay':
        this.watchLastRun();
        break;
      case 'spectate':
        if (this.followWanted) this.stopFollowing();
        else this.spectatePlayer('next');
        break;
      case 'followNext':
      case 'followPrev':
        if (this.followWanted) this.net?.spectate(a === 'followNext' ? 'next' : 'prev');
        break;
      case 'thirdPerson':
        this.store.update({ thirdPerson: !this.settings.thirdPerson });
        break;
      case 'tjlRecord':
        this.command(this.tjlRecorder ? 'tjl stop' : 'tjl record');
        break;
    }
    if (this.net && netAction) this.prediction.afterAction(session);
  }

  /** Spectates the replay of the last finished run. */
  watchLastRun(): boolean {
    const replay = this.lastResult?.replay;
    if (!replay) {
      this.hud.message('No finished run to watch yet', 'info');
      return false;
    }
    this.spectate(replay);
    return true;
  }

  /**
   * Game commands (typed in the chat with "/", like ETJump's console):
   * save / load [1-3], goto / call <player>, tjl record | stop | clear,
   * team axis | allies, reset (training HUDs), spec [player].
   */
  command(line: string): void {
    const [cmd = '', ...rest] = line.trim().split(/\s+/);
    const arg = rest.join(' ');
    const slotAction = (kind: 'save' | 'load') => {
      const n = arg ? Number(arg) : 1;
      if (!(n >= 1 && n <= 3)) return this.notify(`Usage: /${kind} [1-3]`);
      this.perform(n === 1 ? kind : (`${kind}${n}` as Action));
    };
    switch (cmd.toLowerCase()) {
      case 'save':
      case 'load':
        slotAction(cmd.toLowerCase() as 'save' | 'load');
        break;
      case 'goto':
      case 'call': {
        if (!this.net) return this.notify(`/${cmd} needs a server room`);
        const p = this.remotes.find(arg);
        if (!p) return this.notify(arg ? `No player matches "${arg}"` : `Usage: /${cmd} <player>`);
        this.net.teleport(cmd.toLowerCase() as 'goto' | 'call', p.id);
        break;
      }
      case 'tjl':
        this.tjlCommand(arg.toLowerCase());
        break;
      case 'team': {
        const teams = this.map.logic.teams;
        const team = /^ax/i.test(arg) ? 0 : /^al/i.test(arg) ? 1 : null;
        if (team === null) return this.notify(`Usage: /team axis | allies (you are ${TEAM_NAMES[this.map.session.team]})`);
        if (teams.includes(team)) this.stopFollowing();
        if (!this.setTeam(team)) return this.notify(`${TEAM_NAMES[team]} have no spawns on this map (${TEAM_NAMES[teams[0]]} only)`);
        this.notify(`You joined the ${TEAM_NAMES[team]}`);
        break;
      }
      case 'reset':
        this.resetTrainers();
        this.notify('Jump speeds, upmove meter and strafe quality reset');
        break;
      case 'spec': {
        const p = arg ? this.remotes.find(arg) : null;
        if (arg && !p) return this.notify(`No player matches "${arg}"`);
        this.spectatePlayer(p ? p.id : 'next');
        break;
      }
      case 'help':
      case '':
        this.notify('Commands: /save [1-3], /load [1-3], /goto <player>, /call <player>, /spec [player], /tjl record|stop|clear, /team axis|allies, /reset');
        break;
      default:
        this.notify(`Unknown command /${cmd} (try /help)`);
    }
  }

  /** Trickjump lines: record a route, stop (it is shown and kept for the map), clear it. */
  private tjlCommand(arg: string): void {
    switch (arg) {
      case 'record':
        if (this.tjlRecorder) return;
        this.tjlRecorder = new TjlRecorder(this.map.name);
        this.hud.message('Recording trickjump line', 'info', 2000);
        break;
      case 'stop': {
        const rec = this.tjlRecorder;
        if (!rec) return;
        this.tjlRecorder = null;
        const route = rec.finish();
        tjlStore.save(this.map.name, route);
        this.tjl.show(route);
        if (!this.settings.showTjl) this.store.update({ showTjl: true });
        this.hud.message(`Trickjump line saved (${route.trails.length} jumps)`, 'good', 2000);
        break;
      }
      case 'clear':
        this.tjlRecorder = null;
        tjlStore.save(this.map.name, null);
        this.tjl.show(null);
        break;
      default:
        this.notify('Usage: /tjl record | stop | clear');
    }
  }

  /** ETJump resets jump speeds, the upmove meter and strafe quality on respawn. */
  resetTrainers(): void {
    this.jumpSpeeds.reset();
    this.upmove.reset();
    this.strafeQuality.reset();
    this.pollUpmove = false;
  }

  /** After each own move: feed the training HUDs. */
  private trainersAfterMove(before: { groundEntityNum: number; pmType: 'normal' | 'noclip' }, cmd: UserCmd, msec: number): void {
    const session = this.map.session;
    const events = session.lastEvents;
    if (!events) return;
    const ps = session.ps;
    if (events.jumped) {
      this.jumpSpeeds.add(events.jumpSpeed);
      this.pollUpmove = true;
    }
    this.upmove.update(ps, cmd, events, cmd.serverTime);
    if (this.pollUpmove) {
      const last = this.jumpSpeeds.jumps[this.jumpSpeeds.jumps.length - 1];
      if (last) last.upmove = this.upmove.fullDelay;
      if (!this.upmove.jumping) this.pollUpmove = false;
    }
    this.strafeQuality.update(before, cmd, events, msec);
    this.maxSpeed.update(ps);
    this.tjlRecorder?.add(ps.origin, ps.velocity, cmd.upmove > 0, cmd.serverTime);
  }

  /** Spectates a player of the room (by id) or the next one; the server switches us over. */
  spectatePlayer(target: number | 'next'): void {
    if (!this.net) {
      this.hud.message('Spectating needs a server room', 'info');
      return;
    }
    this.stopSpectating();
    this.followWanted = true;
    this.net.spectate(target);
  }

  /**
   * Joins a team and respawns at its spawn (online the server does the same
   * between the same two commands). False when the map has no spawns for it.
   */
  setTeam(team: Team): boolean {
    const session = this.map.session;
    if (!session.logic.teams.includes(team)) return false;
    this.net?.setTeam(team);
    session.setTeam(team);
    if (this.settings.team !== team) this.store.update({ team });
    this.snapInterpolation();
    this.resetTrainers();
    if (this.net) this.prediction.afterAction(session);
    return true;
  }

  /** Back to playing (from the spawn, like the server). */
  stopFollowing(): void {
    if (!this.followWanted) return;
    this.net?.spectate('off');
    this.endFollow();
  }

  private startFollow(): void {
    this.follow = new FollowView(this.map.logic);
    this.ui.dismissRunResult();
    this.ui.spectating(true);
  }

  private endFollow(): void {
    this.followWanted = false;
    if (!this.follow) return;
    this.follow = null;
    this.remotes.hidden = null;
    const session = this.map.session;
    session.respawn();
    // the command clock restarts with the next command, like on the server
    session.ps.commandTime = 0;
    this.prediction.reset();
    const ps = session.ps;
    this.viewAngles = [ps.viewangles[0], ps.viewangles[1], 0];
    session.ps.deltaAngles = [0, 0, 0];
    this.snapInterpolation();
    this.selfModel?.reset(session.anim.eventSeq);
    this.hud.setBanner(null);
    this.ui.spectating(false);
  }

  private followBanner(name: string | null): void {
    const b = this.settings.binds;
    const keys = `${bindLabel(b, 'followNext')} next · ${bindLabel(b, 'followPrev')} previous · ${bindLabel(b, 'spectate')} play · ${bindLabel(b, 'thirdPerson')} 3rd person`;
    this.hud.setBanner(name ? `SPECTATING ${name} — ${keys}` : `SPECTATING — nobody to follow · ${bindLabel(b, 'spectate')} play`);
  }

  /** Follows a replay with the camera, with a timeline: pause, seek, speed. */
  spectate(replay: Replay, label = 'REPLAY'): void {
    this.stopSpectating();
    if (this.followWanted) this.stopFollowing();
    // a solid player model, shown in third person
    const ghost = new Ghost(this.map.logic, this.map.world, this.playerKit, 'solid');
    this.scene.add(ghost.object);
    // the followed run is heard like the player's own
    ghost.session.on((e) => {
      if (e.type === 'sound') this.audio?.sessionSound(e.sound);
    });
    ghost.start(replay);
    this.spectator = ghost;
    this.spectatorAccumulator = 0;
    this.replaySpeed = 1;
    this.replayPaused = false;
    this.replayLabel = label;
    this.replayKeys = new Set(['jump', 'left', 'right', 'forward', 'back'].filter((a) => this.input.active(a as Action)) as Action[]);
    this.ui.dismissRunResult();
    const b = this.settings.binds;
    const k = (a: Action) => keyLabel(b[a][0] ?? b[a][1]);
    this.hud.setBanner(`${label} — ${k('jump')} pause · ${k('left')}/${k('right')} ∓5 s · ${k('forward')}/${k('back')} speed · ${k('replay')} or ${k('respawn')} stop`);
    this.ui.spectating(true);
  }

  stopSpectating(): void {
    if (!this.spectator) return;
    this.scene.remove(this.spectator.object);
    this.spectator.dispose();
    this.spectator = null;
    this.hud.setBanner(null);
    this.hud.setReplay(null);
    this.ui.spectating(false);
  }

  private frame = (now: number): void => {
    if (!this.running) return;
    this.rafId = requestAnimationFrame(this.frame);
    let dt = now - this.lastFrame;
    this.lastFrame = now;
    if (dt > 250) dt = 250; // don't try to catch up after a stall

    if (this.spectator) this.handleSpectatorInput();
    else if (this.follow) this.handleFollowInput();
    else {
      this.handleMouse();
      this.handleActions();
    }

    const msec = this.settings.physicsMsec;
    this.accumulator += dt;
    while (this.accumulator >= msec) {
      this.accumulator -= msec;
      // a spectator has no simulation of its own (the clock still runs for the shaders)
      if (this.follow) this.serverTime += msec;
      else this.tick(msec);
    }
    const spec = this.spectator;
    if (spec) {
      if (!this.replayPaused) this.spectatorAccumulator += dt * this.replaySpeed;
      // at the end the last moment stays on screen (paused)
      while (this.spectatorAccumulator >= spec.msec && spec.position < spec.length) {
        this.spectatorAccumulator -= spec.msec;
        spec.tick();
      }
      if (spec.position >= spec.length) {
        this.spectatorAccumulator = 0;
        this.replayPaused = true;
      }
      this.hud.setReplay({ time: spec.elapsed, total: spec.duration, speed: this.replaySpeed, paused: this.replayPaused, label: this.replayLabel });
    }

    this.render(this.accumulator / msec);

    const board = !this.spectator && this.input.active('scoreboard');
    if (board !== this.scoreboardShown) {
      this.scoreboardShown = board;
      this.ui.scoreboard(board);
    }

    if (this.resultShownAt && now - this.resultShownAt > RESULT_MOVE_GRACE_MS) {
      const m = this.lastMove;
      if (m.forwardmove || m.rightmove || m.upmove) {
        this.resultShownAt = 0;
        this.ui.dismissRunResult();
      }
    }
    if (this.framesToThumbnail > 0 && --this.framesToThumbnail === 0) {
      // right after render(): the drawing buffer still holds this frame
      captureThumbnail(this.renderer.domElement)
        .then((blob) => blob && this.ui.thumbnail(this.map.name, blob))
        .catch((err) => console.warn('thumbnail:', err));
    }

    this.fpsFrames++;
    if (now - this.fpsTime > 500) {
      this.fps = (this.fpsFrames * 1000) / (now - this.fpsTime);
      this.fpsFrames = 0;
      this.fpsTime = now;
    }
  };

  /**
   * Watching a replay: the movement keys drive the timeline (jump pauses, the
   * strafe keys seek 5 s, forward / back change the speed); the replay and
   * respawn keys stop watching; the camera can switch to third person.
   */
  private handleSpectatorInput(): void {
    this.input.takeMouse();
    const spec = this.spectator!;
    for (const a of this.input.takeActions()) {
      if (a === 'thirdPerson' || a === 'help' || a === 'debug' || a === 'chat') this.perform(a);
      else if (a === 'replay' || a === 'respawn' || a === 'spectate') {
        this.stopSpectating();
        this.input.clear();
        return;
      }
    }
    // rising edges of the held keys
    const down = new Set((['jump', 'left', 'right', 'forward', 'back'] as Action[]).filter((a) => this.input.active(a)));
    const pressed = (a: Action) => down.has(a) && !this.replayKeys.has(a);
    if (pressed('jump')) {
      // play again from the start when paused at the end
      if (this.replayPaused && spec.position >= spec.length) spec.seek(0);
      this.replayPaused = !this.replayPaused;
    }
    const step = Math.round(5000 / spec.msec);
    if (pressed('left')) spec.seek(spec.position - step);
    if (pressed('right')) spec.seek(spec.position + step);
    if (pressed('forward')) this.replaySpeed = Math.min(4, this.replaySpeed * 2);
    if (pressed('back')) this.replaySpeed = Math.max(0.25, this.replaySpeed / 2);
    if (pressed('left') || pressed('right')) this.spectatorAccumulator = 0;
    this.replayKeys = down;
    this.input.endTick();
  }

  /** Spectating a player: the camera is theirs; next / previous / play, chat and the HUD keys work. */
  private handleFollowInput(): void {
    this.input.takeMouse();
    const allowed: Action[] = ['followNext', 'followPrev', 'spectate', 'thirdPerson', 'chat', 'help', 'debug', 'voteYes', 'voteNo'];
    for (const a of this.input.takeActions()) if (allowed.includes(a)) this.perform(a);
  }

  private handleMouse(): void {
    const [dx, dy] = this.input.takeMouse();
    const s = this.settings.sensitivity;
    const delta = this.map.session.ps.deltaAngles;
    this.viewAngles[1] -= dx * s * M_YAW;
    this.viewAngles[0] += dy * s * M_PITCH * (this.settings.invertMouse ? -1 : 1);
    // keep the resulting view pitch inside the pmove clamp (CL_ClampPitch)
    const pitchDelta = SHORT2ANGLE(delta[0]);
    this.viewAngles[0] = Math.max(-MAX_PITCH - pitchDelta, Math.min(MAX_PITCH - pitchDelta, this.viewAngles[0]));
    this.viewAngles[1] = ((this.viewAngles[1] % 360) + 360) % 360;
  }

  private handleActions(): void {
    for (const a of this.input.takeActions()) this.perform(a);
  }

  /** A reply to a command: in the chat log online, as a message offline (there is no chat). */
  private notify(text: string): void {
    if (this.net) this.hud.chat(null, text);
    else this.hud.message(text, 'info', 4000);
  }

  /** Chat field: game input is off while typing, Enter sends, Esc cancels; offline it only takes /commands. */
  private openChat(): void {
    const net = this.net;
    if (this.hud.chatting) return;
    this.input.enabled = false;
    this.input.clear();
    this.hud.openChat((text) => {
      // "/command" runs a game command (ETJump console commands), anything else is chat
      if (text?.startsWith('/')) this.command(text.slice(1));
      else if (text && net) net.sendChat(text);
      else if (text) this.notify('Offline: only /commands work here (try /help)');
      this.input.enabled = this.running;
    });
  }

  private tick(msec: number): void {
    const session = this.map.session;
    this.serverTime += msec;
    const before: Vec3 = [...session.ps.origin];
    // the player stands still while a replay is watched
    const move = this.spectator ? { forwardmove: 0, rightmove: 0, upmove: 0 } : this.input.move();
    this.lastMove = move;
    const cmd: UserCmd = {
      serverTime: this.serverTime,
      angles: [ANGLE2SHORT(this.viewAngles[0]), ANGLE2SHORT(this.viewAngles[1]), 0],
      ...move,
      buttons: this.spectator ? 0 : (this.input.active('sprint') ? BUTTON_SPRINT : 0) | (this.input.active('activate') ? BUTTON_ACTIVATE : 0),
      wbuttons: !this.spectator && this.input.active('prone') ? WBUTTON_PRONE : 0,
    };
    session.syncCommandTime(this.serverTime, msec);
    this.prevViewHeight = session.ps.viewheight;
    this.net?.sendCmd(cmd);
    const moveStart = { groundEntityNum: session.ps.groundEntityNum, pmType: session.ps.pmType };
    session.tick(cmd);
    if (session.lastEvents?.landDelta) this.viewWeapon?.land(session.lastEvents.landDelta, performance.now());
    this.trainersAfterMove(moveStart, cmd, msec);
    if (this.net) this.prediction.record(cmd, session);
    this.input.endTick();
    if (this.selfModel) {
      this.selfModel.tick(session.anim, msec);
      this.selfMoves++;
    }

    const ps = session.ps;
    const moved = Math.hypot(ps.origin[0] - before[0], ps.origin[1] - before[1], ps.origin[2] - before[2]);
    this.prevOrigin = moved > 64 ? [...ps.origin] : before;
  }

  private snapInterpolation(): void {
    this.prevOrigin = [...this.map.session.ps.origin];
  }

  /** View angles as pmove sees them: cmd angles + delta_angles (set by teleports, loads, spawns). */
  private renderAngles(): Vec3 {
    const delta = this.map.session.ps.deltaAngles;
    const pitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, this.viewAngles[0] + SHORT2ANGLE(delta[0])));
    return [pitch, this.viewAngles[1] + SHORT2ANGLE(delta[1]), 0];
  }

  /** HUD keys from a command's movement (a followed player). */
  private cmdKeyMask(cmd: { forwardmove: number; rightmove: number; upmove: number; buttons: number; wbuttons: number } | null): number {
    if (!cmd) return 0;
    return (
      (cmd.forwardmove > 0 ? KEY_FORWARD : 0) |
      (cmd.forwardmove < 0 ? KEY_BACK : 0) |
      (cmd.rightmove < 0 ? KEY_LEFT : 0) |
      (cmd.rightmove > 0 ? KEY_RIGHT : 0) |
      (cmd.upmove > 0 ? KEY_JUMP : 0) |
      (cmd.upmove < 0 ? KEY_CROUCH : 0) |
      (cmd.buttons & BUTTON_SPRINT ? KEY_SPRINT : 0) |
      (cmd.wbuttons & WBUTTON_PRONE ? KEY_PRONE : 0)
    );
  }

  private keyMask(): number {
    if (this.spectator) return 0;
    const i = this.input;
    return (
      (i.active('forward') ? KEY_FORWARD : 0) |
      (i.active('back') ? KEY_BACK : 0) |
      (i.active('left') ? KEY_LEFT : 0) |
      (i.active('right') ? KEY_RIGHT : 0) |
      (i.active('jump') ? KEY_JUMP : 0) |
      (i.active('crouch') ? KEY_CROUCH : 0) |
      (i.active('sprint') ? KEY_SPRINT : 0) |
      (i.active('prone') ? KEY_PRONE : 0)
    );
  }

  private render(alpha: number): void {
    const now = performance.now();
    const spec = this.spectator;
    const fol = this.follow?.frame(now) ?? null;
    const settings = this.settings;
    const third = settings.thirdPerson;
    const own = this.map.session;
    // what the camera follows: a watched replay, a spectated player or the player
    let ps: PlayerState;
    let movers: MoverSet;
    let renderTime: number;
    let eye: Vec3;
    let angles: Vec3;
    let origin: Vec3;
    if (spec) {
      const k = Math.min(1, this.spectatorAccumulator / spec.msec);
      const view = spec.view(k);
      ps = spec.session.ps;
      movers = spec.session.movers;
      renderTime = ps.commandTime - spec.msec * (1 - k);
      eye = view.origin;
      angles = view.angles;
      origin = [eye[0], eye[1], eye[2] - (ps.viewheight + 0)];
    } else if (fol) {
      ps = fol.state.ps;
      movers = this.follow!.movers;
      renderTime = fol.commandTime;
      origin = fol.origin;
      eye = [fol.origin[0], fol.origin[1], fol.origin[2] + fol.viewheight];
      angles = fol.angles;
    } else {
      ps = own.ps;
      movers = own.movers;
      renderTime = ps.commandTime - settings.physicsMsec * (1 - alpha);
      const o = ps.origin;
      const p = this.prevOrigin;
      const vh = this.prevViewHeight + (ps.viewheight - this.prevViewHeight) * alpha;
      origin = [p[0] + (o[0] - p[0]) * alpha, p[1] + (o[1] - p[1]) * alpha, p[2] + (o[2] - p[2]) * alpha];
      eye = [origin[0], origin[1], origin[2] + vh];
      angles = this.renderAngles();
    }

    // movers are drawn where their trajectories are at the rendered moment; the
    // physics sees them at the last server frame, so a rider is carried along
    // by the difference (CG_AdjustPositionForMover)
    this.updateMovers(movers, renderTime);
    if (movers.time !== null && movers.has(ps.groundEntityNum)) {
      const at = movers.pose(ps.groundEntityNum, renderTime)!;
      const frame = movers.pose(ps.groundEntityNum, movers.time)!;
      for (let i = 0; i < 3; i++) {
        eye[i] += at.origin[i] - frame.origin[i];
        origin[i] += at.origin[i] - frame.origin[i];
      }
    }

    // third person: the camera behind the eye (CG_OffsetThirdPersonView)
    let camera = { origin: eye, angles };
    if (third) camera = thirdPersonView(eye, angles, settings.thirdPersonRange, settings.thirdPersonAngle, (s, mins, maxs, e) => this.map.world.trace(s, mins, maxs, e, MASK_SOLID));
    this.camera.position.set(camera.origin[0], camera.origin[1], camera.origin[2]);
    const forward: Vec3 = [0, 0, 0];
    const right: Vec3 = [0, 0, 0];
    const up: Vec3 = [0, 0, 0];
    angleVectors(camera.angles, forward, right, up);
    const m = new THREE.Matrix4().makeBasis(
      new THREE.Vector3(right[0], right[1], right[2]),
      new THREE.Vector3(up[0], up[1], up[2]),
      new THREE.Vector3(-forward[0], -forward[1], -forward[2]),
    );
    this.camera.quaternion.setFromRotationMatrix(m);
    this.camera.updateMatrixWorld();

    const cameraPos = camera.origin;
    // the spectated player is drawn by the remote players, except in first person
    this.remotes.hidden = fol && !third ? fol.state.id : null;
    this.remotes.render(now, cameraPos);
    if (spec) {
      spec.object.visible = third;
      if (third) spec.render(Math.min(1, this.spectatorAccumulator / spec.msec), now, cameraPos);
    }
    this.renderSelf(third && !spec && !this.follow, origin, angles, now, cameraPos);
    this.map.scene.update(this.serverTime / 1000);
    this.playerKit?.update(this.serverTime / 1000);
    this.map.scene.updateVisibility(cameraPos);
    this.map.scene.sky?.follow(this.camera);
    this.renderer.render(this.scene, this.camera);
    // the world pass's statistics (the next render resets them)
    const { calls, triangles } = this.renderer.info.render;
    // the knife in hand, over the world (first person only, not for the dead or a free spectator camera)
    const viewed = spec ? spec.session : fol ? null : own;
    if (!third && settings.drawGun && (fol ? fol.state.health : (viewed?.health ?? 0)) > 0) {
      const vw = this.ensureViewWeapon();
      vw?.render(this.renderer, this.camera, { eye: camera.origin, angles: camera.angles, velocity: ps.velocity, bobCycle: viewed?.steps.bobCycle ?? 0, team: fol ? (fol.state.team ?? 0) : viewed!.team, fov: settings.fov, time: now });
    }
    this.audio?.frame({ origin: cameraPos, forward, left: [-right[0], -right[1], -right[2]], up }, movers, renderTime, now);
    this.hud.debugText = `draw ${calls} calls · ${Math.round(triangles / 1000)}k tris · cluster ${this.map.scene.cluster}`;

    if (this.follow) this.followBanner(fol ? fol.state.name : this.follow.target === null ? null : this.follow.name);
    const cmd = fol ? fol.state.cmd : null;
    const followTimer = fol?.state.timer ? { run: fol.state.timer.run, time: fol.state.timer.time + (fol.commandTime - fol.state.ps.commandTime) } : null;
    this.hud.update({
      speed: Math.hypot(ps.velocity[0], ps.velocity[1]),
      velocityZ: ps.velocity[2],
      health: fol ? fol.state.health : own.health,
      timer: spec ? { run: 'replay', time: spec.elapsed, label: this.replayLabel } : this.follow ? followTimer : own.timerun ? { run: own.timerun.run, time: own.time - own.timerun.startTime } : null,
      fps: this.fps,
      origin: ps.origin,
      angles,
      noclip: ps.pmType === 'noclip',
      keys: this.follow ? this.cmdKeyMask(cmd) : this.keyMask(),
      extra: `team ${TEAM_NAMES[this.map.session.team]}${this.net ? ` · online: ${this.remotes.names.length + 1} players · corrections ${this.prediction.corrections}` : ''}`,
    });
    const move = this.follow ? (cmd ?? { forwardmove: 0, rightmove: 0, upmove: 0 }) : this.lastMove;
    const sprintHeld = this.follow ? !!cmd && (cmd.buttons & BUTTON_SPRINT) !== 0 : this.input.active('sprint');
    // ETJump HUDs: the overbounce detector and snaphud follow the camera's player, the rest the own moves
    const fwd: Vec3 = [0, 0, 0];
    angleVectors(angles, fwd, null, null);
    const own3 = !spec && !this.follow;
    this.hud.drawEtj({
      ob:
        settings.hud.obDetector && ps.pmType === 'normal'
          ? detectOverbounce(ps, eye, fwd, settings.physicsMsec, this.map.logic.rules.noOverbounce, (s, e, mask) => this.map.world.trace(s, [0, 0, 0], [0, 0, 0], e, mask || MASK_PLAYERSOLID), settings.hud.obSticky)
          : null,
      jumps: own3 ? this.jumpSpeeds.jumps : null,
      snap: !spec && (!this.follow || fol) && ps.pmType === 'normal' ? { yaw: angles[1], move, sprint: sprintHeld, msec: settings.physicsMsec, hfov: settings.fov } : null,
      upmove: own3 ? { pre: this.upmove.preDelay, post: this.upmove.postDelay, full: this.upmove.fullDelay } : null,
      strafeQuality: own3 ? this.strafeQuality.quality : null,
      maxSpeed: own3 ? this.maxSpeed.shown : null,
    });
    this.hud.drawCgaz(
      !spec && (!this.follow || fol) && settings.hud.cgaz && ps.pmType === 'normal'
        ? {
            yaw: angles[1],
            velocity: [ps.velocity[0], ps.velocity[1]],
            move,
            sprint: sprintHeld && ps.sprintTime > 50,
            onGround: ps.groundEntityNum !== ENTITYNUM_NONE,
            frametime: settings.physicsMsec / 1000,
            hfov: settings.fov,
          }
        : null,
    );
  }

  /** The first person knife, made with the player model kit the first time it is drawn. */
  private ensureViewWeapon(): ViewWeapon | null {
    if (!this.viewWeapon && this.playerKit?.assets.viewWeapon) this.viewWeapon = new ViewWeapon(this.playerKit, this.playerKit.assets.viewWeapon);
    return this.viewWeapon;
  }

  /** The player's own character in third person (created when first needed). */
  private renderSelf(show: boolean, origin: Vec3, angles: Vec3, now: number, cameraPos: Vec3): void {
    if (!show) {
      if (this.selfModel) this.selfModel.object.visible = false;
      return;
    }
    if (!this.selfModel) {
      this.selfModel = new SessionModel(this.playerKit, 'solid', this.net?.id ?? 0);
      this.selfModel.reset(this.map.session.anim.eventSeq);
      this.scene.add(this.selfModel.object);
    }
    const session = this.map.session;
    this.selfModel.object.visible = session.ps.pmType !== 'noclip';
    this.selfModel.render(session.ps, session.anim, origin, angles, this.selfMoves, now, cameraPos);
  }

  /** Places the drawn brush models of the followed player's movers. */
  private updateMovers(movers: LoadedMap['session']['movers'], time: number): void {
    if (movers.time === null) return;
    const fwd: Vec3 = [0, 0, 0];
    const right: Vec3 = [0, 0, 0];
    const up: Vec3 = [0, 0, 0];
    for (const d of movers.defs) {
      const group = this.map.scene.entityGroups.get(d.entityNum);
      const pose = movers.pose(d.entityNum, time);
      if (!group || !pose) continue;
      // model -> world: columns forward, left, up of the angles (the inverse of CreateRotationMatrix)
      angleVectors(pose.angles, fwd, right, up);
      group.matrixAutoUpdate = false;
      group.matrix.set(fwd[0], -right[0], up[0], pose.origin[0], fwd[1], -right[1], up[1], pose.origin[1], fwd[2], -right[2], up[2], pose.origin[2], 0, 0, 0, 1);
      group.matrixWorldNeedsUpdate = true;
    }
  }

  private showResult(r: RunResult): void {
    this.lastResult = r;
    this.resultShownAt = performance.now();
    this.ui.runResult(r);
  }

  private onNet = (m: ServerMessage): void => {
    switch (m.t) {
      case 'snapshot':
        // shared movers: what the others did, at the frames our movers apply it
        if (m.moverEvents) this.map.session.movers.events.push(...m.moverEvents);
        if (m.you) {
          // playing (again): the server ended spectating (a map change, the followed room emptied)
          if (this.follow) this.endFollow();
          this.prediction.reconcile(this.map.session, m.you);
        } else if (this.followWanted) {
          if (!this.follow) this.startFollow();
          if (m.follow) {
            this.follow!.add(m.follow, performance.now());
            // its sounds play at the delay its movement is drawn with
            const sounds = m.follow.sounds;
            if (sounds.length && this.audio) setTimeout(() => sounds.forEach((snd) => this.follow && this.audio?.sessionSound(snd)), FOLLOW_DELAY);
          }
        }
        this.remotes.update(m.players, performance.now());
        if (performance.now() - this.lastPlayersUpdate > 250) {
          this.lastPlayersUpdate = performance.now();
          this.ui.players(m.players);
        }
        break;
      case 'info':
        this.hud.chat(null, m.text);
        break;
      case 'chat':
        this.hud.chat(m.from, m.text);
        break;
      case 'error':
        this.hud.message(m.text, 'bad');
        break;
    }
  };

  private onSessionEvent = (e: SessionEvent): void => {
    const mapName = this.map.name;
    switch (e.type) {
      case 'timerStart':
        if (e.restart) break;
        this.hud.clearRunInfo();
        this.hud.message(`${e.run === 'default' ? 'Run' : `"${e.run}"`} started`, 'info', 2500);
        break;
      case 'checkpoint':
        this.hud.split(`CP ${e.index + 1}`, e.time);
        break;
      case 'timerStop': {
        const splits = e.checkpoints.map((time, index) => ({ index, time }));
        this.showResult({ id: nextResultId++, map: mapName, run: e.run, time: e.time, online: !!this.net, splits, replay: e.replay });
        break;
      }
      case 'timerInterrupted':
        this.hud.clearRunInfo();
        this.hud.message(`Run interrupted (${e.reason})`, 'bad');
        break;
      case 'death':
        this.hud.message('You died', 'bad');
        this.resetTrainers();
        break;
      case 'saved':
        this.hud.message('Position saved', undefined, 1500);
        break;
      case 'loaded':
        this.hud.message('Position loaded', undefined, 1500);
        this.maxSpeed.show(performance.now());
        break;
      case 'message':
        // map messages (target_print) carry ET colour codes and \n line breaks
        this.hud.message(stripColors(e.text).replace(/\\n|\n/g, ' ').trim());
        break;
      case 'sound':
        // the player's own sounds play while no replay is watched
        if (!this.spectator) this.audio?.sessionSound(e.sound);
        break;
    }
  };
}
